import { Component } from 'react';
import type { ReactNode } from 'react';

import { logger } from '../lib/utils/logger';
import { categorizeError, ErrorType } from '../utils/error-utils';
import type { SessionManager } from '../lib/session';
import { isAuthenticatedState } from '../lib/session';
import { userSession, adminSession } from '../lib/session/instances';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isSessionEnded: boolean;
}

/**
 * Factory for the authentication render-error boundary, closed over a specific
 * per-domain SessionManager.
 *
 * This boundary is deliberately NOT a logout authority. Deciding a session is
 * dead belongs solely to the per-domain SessionManager (its `markRefreshFailed`
 * is the only setter of `Dead`, and it always emits the `session_expired`
 * directive); rendering the logout/redirect UX belongs solely to the app-root
 * DirectiveHandler (a sibling mounted above this boundary). Concentrating the
 * decision there is what stops the recurring "spurious logout" bugs that came
 * from several components each deciding to log out on their own. See
 * documentation/architecture/session-management-redesign.md and CU-868kyhab4.
 *
 * A 401 that reaches render is handled by deferring to the SessionManager:
 * - session still alive (Alive || Refreshing) → the 401 is stale/recovered;
 *   swallow it so the subtree re-renders (its queries refetch with the fresh
 *   cookie). The client that originally 401'd already reported the anomaly via
 *   reportAnomaly401, so no further action is needed here.
 * - session already dead → the DirectiveHandler modal owns the UX; render a
 *   quiet placeholder (which unmounts the throwing subtree).
 * Non-auth errors re-throw to the outer ErrorBoundary. This boundary never
 * calls logout or redirect — that authority lives only in the directive path.
 *
 * Why a factory: `getDerivedStateFromError` is static and cannot read instance
 * props, so the domain SessionManager is captured in the closure instead. One
 * implementation then serves both the user and admin trees rather than being
 * hardcoded to userSession (CU-868kykuzk).
 */
function createAuthErrorBoundary(session: SessionManager) {
  return class AuthErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
      super(props);
      this.state = { hasError: false, error: null, isSessionEnded: false };
    }

    static getDerivedStateFromError(error: Error): State {
      const isAuthError = categorizeError(error).type === ErrorType.AUTHENTICATION;

      if (!isAuthError) {
        // Not our concern — surface it so render() re-throws to the outer boundary.
        return { hasError: true, error, isSessionEnded: false };
      }

      if (isAuthenticatedState(session.state)) {
        // Stale/recovered 401 against a live session — swallow so the subtree
        // re-renders. If it keeps throwing, React stops retrying this boundary and
        // re-throws to the outer ErrorBoundary; still never a logout from here.
        return { hasError: false, error: null, isSessionEnded: false };
      }

      // Session is gone; the DirectiveHandler modal owns the UX. Render a quiet
      // placeholder (unmounts the throwing subtree). We never log out here.
      return { hasError: true, error, isSessionEnded: true };
    }

    override componentDidCatch(error: Error): void {
      if (categorizeError(error).type !== ErrorType.AUTHENTICATION) {
        logger.debug('Non-auth error in AuthErrorBoundary; re-throwing to outer ErrorBoundary');
        return;
      }

      // React only calls componentDidCatch when getDerivedStateFromError returned
      // an error state — i.e. the swallow (session-alive) path bypasses this. So
      // reaching here means the session was NOT alive: the SessionManager has
      // already declared death and emitted session_expired, and the DirectiveHandler
      // owns the redirect. Instrumentation only (CU-868kygk35) — no logout here.
      logger.info(
        '[auth-boundary] authentication error against an ended session; deferring to DirectiveHandler',
        {
          domain: session.domain,
          sessionState: session.state,
        },
      );
    }

    override render() {
      if (this.state.hasError) {
        if (this.state.isSessionEnded) {
          // Quiet placeholder behind the DirectiveHandler's "Session Expired"
          // modal. No action here — the modal owns the redirect.
          return (
            <div className="min-h-screen flex items-center justify-center surface-secondary">
              <p className="text-secondary text-sm">Your session has ended.</p>
            </div>
          );
        }
        // Non-auth error — re-throw to the outer ErrorBoundary.
        throw this.state.error;
      }

      return this.props.children;
    }
  };
}

/** User-domain boundary (bound to userSession). Mounted around the app routes. */
export const AuthErrorBoundary = createAuthErrorBoundary(userSession);

/** Admin-domain boundary (bound to adminSession). Mounted around the admin routes. */
export const AdminAuthErrorBoundary = createAuthErrorBoundary(adminSession);
